import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { trackLanguageChange } from '@/utils/analytics';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Car, Menu, X, Globe, LogOut, Settings, Shield, Crown, Moon, Sun, ChevronDown, Bot, Lightbulb, BookOpenText, HelpCircle, FileText, Zap, LayoutDashboard, Sparkles, ScrollText, PlayCircle, MapPin } from 'lucide-react';
import { routeMappings, basePathToKey, switchLanguagePath } from '@/utils/localizedRoutes';
import { useTheme } from '@/contexts/ThemeContext';
import { prefetchPageData } from '@/utils/dataPrefetcher';

const prefetchMap = {
  '/': () => import("@/pages/LandingPage"),
  '/teoriprov': () => import("@/pages/TestsPage"),
  '/teorilektioner': () => import("@/pages/LessonsPage"),
  '/trafikmarken': () => import("@/pages/TrafficSignsPage"),
  '/snabba-rad': () => import("@/pages/QuickTipsPage"),
  '/ai-assistent': () => import("@/pages/AssistantPage"),
  '/priser': () => import("@/pages/PricingPage"),
  '/recensioner': () => import("@/pages/ReviewsPage"),
  '/support': () => import("@/pages/SupportPage"),
  '/viktiga-ord': () => import("@/pages/ImportantWordsPage"),
  '/trafikregler': () => import("@/pages/TrafficRulesPage"),
  '/hur-man-anvander': () => import("@/pages/HowToUsePage"),
  '/artiklar': () => import("@/pages/ArticlesPage"),
  '/oversikt': () => import("@/pages/DashboardPage"),
  '/logga-in': () => import("@/pages/LoginPage"),
  '/registrera': () => import("@/pages/RegisterPage"),
};
const prefetched = new Set();

const toolsLabel = {
  sv: 'Verktyg', en: 'Tools', ar: 'أدوات', tr: 'Araçlar',
  fa: 'ابزارها', so: 'Qalabka', pl: 'Narzędzia', es: 'Herramientas', de: 'Werkzeuge'
};

const trafficRulesLabel = {
  sv: 'Trafikregler', en: 'Traffic Rules', ar: 'قواعد المرور', tr: 'Trafik Kuralları',
  fa: 'قوانین رانندگی', so: 'Xeerarka Taraafikada', pl: 'Przepisy drogowe', es: 'Normas de tráfico', de: 'Verkehrsregeln'
};

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileMenuOpen]);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const toolsRef = useRef(null);
  const toolsTimeout = useRef(null);

  const isRTL = i18n.language === 'ar' || i18n.language === 'fa';
  const lang = i18n.language;

  const languages = [
    { code: 'sv', name: 'Svenska', flag: 'se' },
    { code: 'ar', name: 'العربية', flag: 'sa' },
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'tr', name: 'Türkçe', flag: 'tr' },
    { code: 'fa', name: 'فارسی', flag: 'ir' },
    { code: 'so', name: 'Soomaali', flag: 'so' },
    { code: 'pl', name: 'Polski', flag: 'pl' },
    { code: 'es', name: 'Español', flag: 'es' },
    { code: 'de', name: 'Deutsch', flag: 'de' },
  ];

  // Close tools dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLanguage = (lng) => {
    trackLanguageChange(lng);
    // Save language preference then reload immediately - no visual flicker
    localStorage.setItem('i18nextLng', lng);
    const currentPath = window.location.pathname;
    const newPath = switchLanguagePath(currentPath, lng);
    window.location.href = newPath;
  };

  const getLocalizedLink = (basePath) => {
    const currentLang = i18n.language;
    const routes = routeMappings[currentLang] || routeMappings['sv'];
    const routeKey = basePathToKey[basePath];
    if (routeKey && routes[routeKey]) return routes[routeKey];
    return basePath;
  };

  const handlePrefetch = useCallback((basePath) => {
    if (prefetched.has(basePath)) return;
    const loader = prefetchMap[basePath];
    if (loader) {
      prefetched.add(basePath);
      loader().catch(() => { prefetched.delete(basePath); });
    }
    // Also prefetch the API data for this page
    prefetchPageData(basePath);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
    window.location.href = '/';
  };

  const isActive = (path) => {
    const localizedPath = getLocalizedLink(path);
    return location.pathname === path || location.pathname === localizedPath;
  };

  const isToolsActive = ['/ai-assistent', '/viktiga-ord', '/snabba-rad', '/hur-man-anvander', '/artiklar'].some(p => isActive(p));

  // Short navbar labels per language. Long translations like
  // "اختبارات تدريبية" / "Pratik testler" overflow the top bar, so we
  // override them with single-word equivalents here. Falls back to the
  // i18n string when no override is configured for a language.
  const NAV_LABELS = {
    tests: { sv: 'Övningsprov', ar: 'الاختبارات', en: 'Tests', tr: 'Testler', fa: 'آزمون‌ها', so: 'Imtixaano', pl: 'Testy', es: 'Tests', de: 'Tests' },
    lessons: { sv: 'Teorilektioner', ar: 'الدروس', en: 'Lessons', tr: 'Dersler', fa: 'درس‌ها', so: 'Casharrada', pl: 'Lekcje', es: 'Lecciones', de: 'Lektionen' },
    signs: { sv: 'Trafikmärken', ar: 'الإشارات', en: 'Signs', tr: 'İşaretler', fa: 'علائم', so: 'Calaamado', pl: 'Znaki', es: 'Señales', de: 'Schilder' },
    videos: { sv: 'Videor', ar: 'فيديوهات', en: 'Videos', tr: 'Videolar', fa: 'ویدئوها', so: 'Fiidiyowyo', pl: 'Filmy', es: 'Videos', de: 'Videos' },
  };
  const navLabel = (key) => NAV_LABELS[key]?.[i18n.language] || NAV_LABELS[key]?.sv;

  // Main nav links (clean & minimal)
  const mainLinks = isAuthenticated ? [
    { to: '/', label: t('nav.home'), icon: null },
    { to: '/teoriprov', label: navLabel('tests'), icon: null },
    { to: '/teorilektioner', label: navLabel('lessons'), icon: null },
    { to: '/trafikmarken', label: navLabel('signs'), icon: null },
    { to: '/teorivideor', label: navLabel('videos'), icon: null },
  ] : [
    { to: '/', label: t('nav.home'), icon: null },
    { to: '/teoriprov', label: navLabel('tests'), icon: null, locked: true },
    { to: '/teorilektioner', label: navLabel('lessons'), icon: null, locked: true },
    { to: '/trafikmarken', label: navLabel('signs'), icon: null, locked: true },
    { to: '/teorivideor', label: navLabel('videos'), icon: null, locked: true },
  ];

  // Tools dropdown items
  const toolsItems = [
    // Free tools (always accessible)
    { to: '/ai-assistent', label: t('ui.aiAssistant'), icon: Bot, desc: { sv: 'Fråga vår AI-lärare', en: 'Ask our AI tutor', ar: 'اسأل معلمنا الذكي', tr: 'AI öğretmenimize sorun', fa: 'از معلم AI بپرسید', so: 'Weydii macalinka AI', pl: 'Zapytaj AI', es: 'Pregunta al AI', de: 'Fragen Sie den KI-Tutor' }, locked: false },
    { to: '/viktiga-ord', label: t('ui.importantWords'), icon: BookOpenText, desc: { sv: 'Viktiga körkortstermer', en: 'Key driving terms', ar: 'مصطلحات القيادة الهامة', tr: 'Önemli sürüş terimleri', fa: 'اصطلاحات مهم رانندگی', so: 'Ereyada muhiimka', pl: 'Ważne terminy', es: 'Términos importantes', de: 'Wichtige Begriffe' }, locked: false },
    { to: '/artiklar', label: { sv: 'Artiklar', en: 'Articles', ar: 'مقالات', tr: 'Makaleler', fa: 'مقالات', so: 'Maqaallada', de: 'Artikel', es: 'Artículos', pl: 'Artykuły' }[i18n.language] || 'Articles', icon: FileText, desc: { sv: 'Tips och råd för teoriprovet', en: 'Tips and advice for the theory test', ar: 'نصائح ومقالات للنجاح بالتيوري', tr: 'Sınav ipuçları ve tavsiyeler', fa: 'نکات و مشاوره آزمون', so: 'Talooyinka imtixaanka', pl: 'Porady do egzaminu', es: 'Consejos para el examen', de: 'Tipps für die Prüfung' }, locked: false },
    // Premium tools (require subscription)
    { to: '/snabba-rad', label: t('ui.quickTips'), icon: Lightbulb, desc: { sv: 'Gyllene tips för provet', en: 'Golden tips for the test', ar: 'نصائح ذهبية للاختبار', tr: 'Sınav için altın ipuçları', fa: 'نکات طلایی آزمون', so: 'Talooyinka dahabiga', pl: 'Złote porady', es: 'Consejos de oro', de: 'Goldene Tipps' }, locked: !isAuthenticated },
    { to: '/interaktiv-karta', label: { sv: 'Interaktiv karta', en: 'Interactive map', ar: 'الخريطة التفاعلية', tr: 'Etkileşimli harita', fa: 'نقشه تعاملی', so: 'Khariidad isdhexgal ah', pl: 'Mapa interaktywna', es: 'Mapa interactivo', de: 'Interaktive Karte' }[i18n.language] || 'Interaktiv karta', icon: MapPin, desc: { sv: 'Öva trafiksituationer', en: 'Practise traffic situations', ar: 'تدرّب على مواقف المرور', tr: 'Trafik durumlarını çalış', fa: 'تمرین موقعیت‌های ترافیکی', so: 'Ku tababar xaaladaha taraafikada', pl: 'Ćwicz sytuacje drogowe', es: 'Practica situaciones de tráfico', de: 'Verkehrssituationen üben' }, locked: !isAuthenticated },
    // How to use - last item
    { to: '/hur-man-anvander', label: t('ui.howToUse'), icon: HelpCircle, desc: { sv: 'Så använder du plattformen', en: 'How to use the platform', ar: 'كيف تستخدم المنصة', tr: 'Platformu nasıl kullanırsınız', fa: 'نحوه استفاده از پلتفرم', so: 'Sida loo isticmaalo', pl: 'Jak korzystać', es: 'Cómo usar', de: 'So nutzen Sie es' }, locked: false },
  ];

  // Right-side link
  const pricingLink = { to: '/priser', label: t('ui.prices') };

  // Mobile: all links flat
  const mobileMainLinks = isAuthenticated ? [
    { to: '/', label: t('nav.home') },
    { to: '/teoriprov', label: navLabel('tests') },
    { to: '/teorilektioner', label: navLabel('lessons') },
    { to: '/trafikmarken', label: navLabel('signs') },
    { to: '/teorivideor', label: navLabel('videos') },
  ] : [
    { to: '/', label: t('nav.home') },
    { to: '/teoriprov', label: navLabel('tests'), locked: true },
    { to: '/teorilektioner', label: navLabel('lessons'), locked: true },
    { to: '/trafikmarken', label: navLabel('signs'), locked: true },
    { to: '/teorivideor', label: navLabel('videos'), locked: true },
  ];

  const handleToolsEnter = () => {
    clearTimeout(toolsTimeout.current);
    setToolsOpen(true);
  };
  const handleToolsLeave = () => {
    toolsTimeout.current = setTimeout(() => setToolsOpen(false), 200);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#18181b]/95 backdrop-blur-md border-b border-slate-200/80 dark:border-[#27272a]" dir={isRTL ? 'rtl' : 'ltr'} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to={getLocalizedLink('/')} onClick={() => { setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2 flex-shrink-0" aria-label="DriveMaster - Go to homepage" data-testid="navbar-logo">
            <div className="w-9 h-9 bg-emerald-500 rounded-xl flex items-center justify-center shadow-sm">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-800 dark:text-gray-100 text-lg">DriveMaster</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {mainLinks.map((link) => (
              <Link
                key={link.to}
                to={getLocalizedLink(link.to)}
                state={link.to === '/teoriprov' ? { resetTests: true } : undefined}
                onMouseEnter={() => handlePrefetch(link.to)}
                data-testid={`nav-link-${link.to.replace(/\//g, '')}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-1 ${
                  isActive(link.to)
                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/70 dark:bg-emerald-950/30'
                    : 'text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
              >
                {link.label}
                {link.locked && <Crown className="w-3 h-3 text-amber-500 transform rotate-12" strokeWidth={2.5} />}
              </Link>
            ))}

            {/* Traffic Rules - placed before Tools */}
            <Link
              to={getLocalizedLink('/trafikregler')}
              onMouseEnter={() => handlePrefetch('/trafikregler')}
              data-testid="nav-link-traffic-rules"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                isActive('/trafikregler')
                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/70 dark:bg-emerald-950/30'
                  : 'text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              {trafficRulesLabel[lang] || trafficRulesLabel.en}
            </Link>

            {/* Tools Dropdown */}
            <div ref={toolsRef} className="relative" onMouseEnter={handleToolsEnter} onMouseLeave={handleToolsLeave}>
              <button
                onClick={() => setToolsOpen(!toolsOpen)}
                data-testid="nav-tools-dropdown"
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-1 cursor-pointer ${
                  isToolsActive || toolsOpen
                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/70 dark:bg-emerald-950/30'
                    : 'text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
              >
                {toolsLabel[lang] || toolsLabel.en}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${toolsOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Tools Mega Dropdown */}
              {toolsOpen && (
                <div
                  className={`absolute top-full mt-2 w-72 bg-white dark:bg-[#1e1e22] rounded-xl shadow-xl border border-slate-200/80 dark:border-[#333] p-2 animate-in fade-in slide-in-from-top-2 duration-200 ${isRTL ? 'right-0' : 'left-0'}`}
                  onMouseEnter={handleToolsEnter}
                  onMouseLeave={handleToolsLeave}
                >
                  {toolsItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.to}
                        to={getLocalizedLink(item.to)}
                        onClick={() => setToolsOpen(false)}
                        onMouseEnter={() => handlePrefetch(item.to)}
                        data-testid={`tools-item-${item.to.replace(/\//g, '')}`}
                        className={`flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group ${
                          isActive(item.to)
                            ? 'bg-emerald-50 dark:bg-emerald-950/30'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                          isActive(item.to)
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/40 group-hover:text-emerald-600 dark:group-hover:text-emerald-400'
                        }`}>
                          <Icon className="w-4.5 h-4.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-sm font-semibold ${isActive(item.to) ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-200'}`}>
                              {item.label}
                            </span>
                            {(item.locked || item.showCrown) && <Crown className="w-3 h-3 text-amber-500 transform rotate-12 flex-shrink-0" strokeWidth={2.5} />}
                          </div>
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">
                            {item.desc?.[lang] || item.desc?.en || ''}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pricing */}
            <Link
              to={getLocalizedLink(pricingLink.to)}
              onMouseEnter={() => handlePrefetch(pricingLink.to)}
              data-testid="nav-link-pricing"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                isActive(pricingLink.to)
                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/70 dark:bg-emerald-950/30'
                  : 'text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              {pricingLink.label}
            </Link>

            {/* Support */}
            <Link
              to={getLocalizedLink('/support')}
              onMouseEnter={() => handlePrefetch('/support')}
              data-testid="nav-link-support"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                isActive('/support')
                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/70 dark:bg-emerald-950/30'
                  : 'text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              {t('support.title')}
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-1.5">

            {/* Theme Toggle */}
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="p-2 hover:bg-slate-100 dark:hover:bg-[#27272a] rounded-full transition-colors"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-500" />}
            </button>

            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="language-selector" className="p-2 hover:bg-slate-100 dark:hover:bg-[#27272a] rounded-full transition-colors" aria-label="Select language">
                  <Globe className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white dark:bg-[#1e1e22]">
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => changeLanguage(l.code)}
                    className={`cursor-pointer ${i18n.language === l.code ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''}`}
                  >
                    <img src={`https://flagcdn.com/w40/${l.flag}.png`} srcSet={`https://flagcdn.com/w80/${l.flag}.png 2x`} alt={l.name} width="20" height="15" className="rounded-sm me-2 inline-block object-cover" loading="lazy" />
                    {l.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-[#27272a] rounded-lg transition-colors" aria-label={`User menu - ${user?.name || 'User'}`}>
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 hidden sm:block">{user?.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-[#1e1e22]">
                  <div className="px-3 py-2 border-b dark:border-[#333]">
                    <p className="font-medium text-sm truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  {user?.role === 'super_admin' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate(getLocalizedLink('/admin-panel'))} className="cursor-pointer text-purple-600">
                        <Shield className="w-4 h-4 me-2" />Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate(getLocalizedLink('/oversikt'))} className="cursor-pointer">
                    <LayoutDashboard className="w-4 h-4 me-2" />{t('ui.overview')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(getLocalizedLink('/installningar'))} className="cursor-pointer">
                    <Settings className="w-4 h-4 me-2" />{t('ui.settings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 me-2" />{t('ui.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate(getLocalizedLink('/logga-in'))} onMouseEnter={() => handlePrefetch('/logga-in')} className="text-slate-600 dark:text-slate-300 whitespace-nowrap shrink-0">
                  {t('ui.login')}
                </Button>
                <button
                  onClick={() => {
                    // Save intent: redirect to pricing after registration
                    localStorage.setItem('postRegisterRedirect', '/priser');
                    navigate(getLocalizedLink('/registrera'));
                  }}
                  onMouseEnter={() => handlePrefetch('/registrera')}
                  data-testid="navbar-subscribe-btn"
                  className="group relative inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 ring-1 ring-orange-300/40 cta-auto-pulse"
                  style={{
                    backgroundImage: 'linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ea580c 100%)',
                  }}
                >
                  {/* Auto-shine effect (every 4s) */}
                  <span className="absolute inset-0 cta-auto-shine bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 pointer-events-none w-1/2"></span>
                  
                  {/* Hover shine sweep */}
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 pointer-events-none"></span>
                  
                  {/* Sparkle icon with rotation animation */}
                  <Sparkles className="relative z-10 w-3.5 h-3.5 group-hover:rotate-12 transition-transform" strokeWidth={2.5} />
                  
                  {/* Text */}
                  <span className="relative z-10 whitespace-nowrap drop-shadow-sm">{t('ui.subscribeNow')}</span>
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-[#27272a] rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-slate-700 dark:text-slate-300" /> : <Menu className="w-6 h-6 text-slate-700 dark:text-slate-300" />}
            </button>
          </div>
        </div>

        {/* ===== Mobile Menu ===== */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-3 pb-24 border-t dark:border-[#27272a] animate-in slide-in-from-top-1 duration-200 max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain scrollbar-hide" data-testid="mobile-menu-panel">

            {/* Main Links */}
            {mobileMainLinks.map((link) => (
              <Link
                key={link.to}
                to={getLocalizedLink(link.to)}
                state={link.to === '/teoriprov' ? { resetTests: true } : undefined}
                onClick={() => setMobileMenuOpen(false)}
                onTouchStart={() => handlePrefetch(link.to)}
                className={`flex items-center justify-between py-3 px-3 text-base font-medium rounded-xl transition-colors ${
                  isActive(link.to) ? 'text-emerald-600 bg-emerald-50/80 dark:bg-emerald-950/30 dark:text-emerald-400' : 'text-slate-700 dark:text-[#d4d4d8] hover:bg-slate-50 dark:hover:bg-[#27272a]'
                }`}
              >
                <span>{link.label}</span>
                {link.locked && <Crown className="w-4 h-4 text-amber-500 transform rotate-12" strokeWidth={2.5} />}
              </Link>
            ))}

            {/* Traffic Rules - Mobile (before Tools) */}
            <Link
              to={getLocalizedLink('/trafikregler')}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-traffic-rules-link"
              className="flex items-center py-3 px-3 text-base font-medium rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-emerald-600 dark:hover:text-emerald-400"
            >
              {trafficRulesLabel[lang] || trafficRulesLabel.en}
            </Link>

            {/* Tools Section */}
            <div className="mt-1">
              <button
                onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
                data-testid="mobile-tools-toggle"
                className={`flex items-center justify-between w-full py-3 px-3 text-base font-medium rounded-xl transition-colors ${
                  isToolsActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-700 dark:text-[#d4d4d8]'
                }`}
              >
                <span>{toolsLabel[lang] || toolsLabel.en}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileToolsOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileToolsOpen && (
                <div className="ms-2 border-s-2 border-emerald-200 dark:border-emerald-800 ps-2 mb-2 animate-in slide-in-from-top-1 duration-150">
                  {toolsItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.to}
                        to={getLocalizedLink(item.to)}
                        onClick={() => { setMobileMenuOpen(false); setMobileToolsOpen(false); }}
                        onTouchStart={() => handlePrefetch(item.to)}
                        className={`flex items-center gap-3 py-2.5 px-3 rounded-xl transition-colors ${
                          isActive(item.to) ? 'text-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/20 dark:text-emerald-400' : 'text-slate-600 dark:text-[#d4d4d8] hover:bg-slate-50 dark:hover:bg-[#27272a]'
                        }`}
                      >
                        <Icon className="w-4.5 h-4.5 flex-shrink-0" />
                        <span className="text-sm font-medium">{item.label}</span>
                        {(item.locked || item.showCrown) && <Crown className="w-3 h-3 text-amber-500 transform rotate-12 ms-auto" strokeWidth={2.5} />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pricing - Mobile */}
            <Link
              to={getLocalizedLink(pricingLink.to)}
              onClick={() => setMobileMenuOpen(false)}
              onTouchStart={() => handlePrefetch(pricingLink.to)}
              className={`flex items-center py-3 px-3 text-base font-medium rounded-xl transition-colors ${
                isActive(pricingLink.to) ? 'text-emerald-600 bg-emerald-50/80 dark:bg-emerald-950/30 dark:text-emerald-400' : 'text-slate-700 dark:text-[#d4d4d8] hover:bg-slate-50 dark:hover:bg-[#27272a]'
              }`}
            >
              {pricingLink.label}
            </Link>

            {/* Divider + Secondary */}
            <div className="border-t dark:border-[#27272a] mt-2 pt-2 flex items-center gap-1">
              <Link
                to={getLocalizedLink('/recensioner')}
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 text-center py-2.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-lg hover:bg-slate-50 dark:hover:bg-[#27272a] transition-colors"
              >
                {({sv:'Recensioner',en:'Reviews',ar:'التقييمات',tr:'Yorumlar',fa:'نظرات',so:'Dib-u-eegis',pl:'Recenzje',es:'Reseñas',de:'Bewertungen'})[lang] || 'Reviews'}
              </Link>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <Link
                to={getLocalizedLink('/support')}
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 text-center py-2.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-lg hover:bg-slate-50 dark:hover:bg-[#27272a] transition-colors"
              >
                {t('support.title')}
              </Link>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-3 w-full py-3 px-3 text-sm font-medium text-slate-500 dark:text-[#d4d4d8] hover:bg-slate-50 dark:hover:bg-[#27272a] rounded-xl mt-1 transition-colors"
            >
              {isDark ? <Sun className="w-4.5 h-4.5 text-amber-400" /> : <Moon className="w-4.5 h-4.5 text-slate-400" />}
              <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
            </button>

            {/* Auth Actions */}
            {isAuthenticated ? (
              <div className="border-t dark:border-[#27272a] mt-2 pt-2">
                <Link to={getLocalizedLink('/installningar')} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-3 px-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#27272a] rounded-xl">
                  <Settings className="w-4 h-4" />{t('ui.settings')}
                </Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full py-3 px-3 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl">
                  <LogOut className="w-4 h-4" />{t('ui.logout')}
                </button>
              </div>
            ) : (
              <div className="flex gap-3 pt-3 mt-2 border-t dark:border-[#27272a]">
                <Button variant="outline" className="flex-1" onClick={() => { navigate(getLocalizedLink('/logga-in')); setMobileMenuOpen(false); }}>
                  {t('ui.login')}
                </Button>
                <button
                  onClick={() => {
                    localStorage.setItem('postRegisterRedirect', '/priser');
                    navigate(getLocalizedLink('/registrera'));
                    setMobileMenuOpen(false);
                  }}
                  data-testid="navbar-subscribe-btn-mobile"
                  className="flex-1 group relative inline-flex items-center justify-center gap-1 px-2 py-2 text-xs font-bold text-white rounded-full overflow-hidden transition-all duration-300 hover:scale-[1.02] ring-1 ring-orange-300/40 cta-auto-pulse whitespace-nowrap"
                  style={{
                    backgroundImage: 'linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ea580c 100%)',
                  }}
                >
                  <span className="absolute inset-0 cta-auto-shine bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 pointer-events-none w-1/2"></span>
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 pointer-events-none"></span>
                  <Sparkles className="relative z-10 w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="relative z-10 drop-shadow-sm truncate">{t('ui.subscribeNow')}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
